import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Cookies from "js-cookie";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { FiUser, FiLock, FiLogIn } from "react-icons/fi";
import API_BASE_URL from "../../api/config";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const MySwal = withReactContent(Swal);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/sign_up/login`,
        {
          email,
          password,
        },
        {
          withCredentials: true,
        },
      );

      if (
        response.status === 200 &&
        (response.data.message === "Login successful!" ||
          response.data.status === "success")
      ) {
        const payload = response.data.data || response.data;
        const { username, session_id, role } = payload;
        const userEmail = payload.email || email;
        const userRole = role || "student";

        const expiresValue = rememberMe ? 7 : 1;
        const cookieOptions = {
          expires: expiresValue,
          path: "/",
          sameSite: "Lax", // Protects against CSRF while remaining functional
          secure: window.location.protocol === "https:",
        };

        Cookies.set("ec_auth_token", session_id, cookieOptions);
        Cookies.set("email", userEmail, cookieOptions);
        Cookies.set("username", username, cookieOptions);
        Cookies.set("role", userRole, cookieOptions);

        if (userRole === "facilitator") {
          Cookies.set("Mentorsemail", userEmail, cookieOptions);
        }

        MySwal.fire({
          title: `<strong>Welcome, ${username}!</strong>`,
          html: "You are being redirected to your Profile Edit page.",
          icon: "success",
          timer: 5000,
          timerProgressBar: true,
          showConfirmButton: false,
        }).then(() => {
          setTimeout(() => {
            navigate("/dashboard/profileEdit");
          }, 500);
        });
      } else {
        // Handle API returning 200 but logical failure
        throw new Error(response.data.message || "Invalid credentials!");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Invalid credentials!";

      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: errorMsg,
        customClass: {
          popup: "swal-popup-custom",
          title: "swal-title-custom",
          content: "swal-content-custom",
        },
      });
      console.error("Login error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Forgot Password Logic (Kept from 2nd Component as requested) ---
  const handleForgotPassword = async () => {
    const { value: emailInput } = await MySwal.fire({
      title: "Enter Your Registered Email Address",
      input: "email",
      inputPlaceholder: "your@email.com",
      showCancelButton: true,
      confirmButtonText: "Send OTP",
      preConfirm: async (email) => {
        if (!email) return false;
        try {
          // Note: You might need to pass the 'role' here if your backend requires it,
          // but since we don't have a selector, we assume the backend handles it by email.
          const res = await axios.post(
            `${API_BASE_URL}/api/forget_password/send_otp`,
            { email },
          );
          if (res.status === 200 && res.data.message?.includes("OTP")) {
            return email;
          } else {
            throw new Error(res.data?.message || "Try Again after sometime.");
          }
        } catch (err) {
          Swal.showValidationMessage(err.message);
        }
      },
    });

    if (!emailInput) return;

    const { value: otp } = await MySwal.fire({
      title: "Enter the Code Sent to Your Registered Email",
      input: "text",
      inputPlaceholder: "Enter OTP",
      showCancelButton: true,
      confirmButtonText: "Verify OTP",
      preConfirm: async (otp) => {
        try {
          const res = await axios.post(
            `${API_BASE_URL}/api/forget_password/verify_otp`,
            { email: emailInput, otp },
          );
          if (res.status === 200 && res.data.message?.includes("OTP")) {
            return otp;
          } else {
            throw new Error(res.data?.message || "Try Again after sometime.");
          }
        } catch (err) {
          Swal.showValidationMessage(err.message);
        }
      },
    });

    if (!otp) return;

    const { value: newPassword } = await MySwal.fire({
      title: "Create a New Password",
      input: "password",
      inputPlaceholder: "New password",
      showCancelButton: true,
      confirmButtonText: "Reset Password",
      preConfirm: async (password) => {
        if (!password || password.length < 6 || password.length > 12) {
          Swal.showValidationMessage(
            "Password length must be in between 6 - 12",
          );
          return false;
        }
        try {
          const res = await axios.post(
            `${API_BASE_URL}/api/forget_password/reset_password`,
            { email: emailInput, new_password: password },
          );
          if (res.status === 200 && res.data.message?.includes("success")) {
            return true;
          } else {
            throw new Error("Failed to reset password.");
          }
        } catch (err) {
          Swal.showValidationMessage(err.message);
        }
      },
    });

    if (newPassword) {
      Swal.fire({
        icon: "success",
        title: "Password Reset Successful",
        text: "You can now log in with your new password.",
      });
    }
  };

  // --- Framer Motion Variants (UI from 2nd Component) ---
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <motion.div
      className="login-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="background-shapes">
        <div className="shape shape1"></div>
        <div className="shape shape2"></div>
        <div className="shape shape3"></div>
      </div>
      <motion.div className="login-box" variants={itemVariants}>
        <div className="login-header">
          <motion.h1
            className="brand"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            Ethiccraft
          </motion.h1>
          <motion.p
            className="tagline"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            Welcome back to the Leading Society.
          </motion.p>
        </div>

        <form onSubmit={handleLogin} className="login-form">
          <motion.div className="input-group" variants={itemVariants}>
            <FiUser className="input-icon" />
            <input
              type="email"
              placeholder="Email Address"
              className="input-box"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </motion.div>
          <motion.div className="input-group" variants={itemVariants}>
            <FiLock className="input-icon" />
            <input
              type="password"
              placeholder="Password"
              className="input-box"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </motion.div>

          <motion.div className="actions" variants={itemVariants}>
            <label className="remember-me">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={() => setRememberMe(!rememberMe)}
              />
              Remember Me
            </label>
            <span
              className="forgot-password"
              onClick={handleForgotPassword}
              style={{ cursor: "pointer", color: "#007bff" }}
            >
              Forgot?
            </span>
          </motion.div>

          <motion.button
            className="login-btn"
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            variants={itemVariants}
          >
            {isSubmitting ? (
              <div className="loader"></div>
            ) : (
              <>
                <FiLogIn />
                <span>Log In</span>
              </>
            )}
          </motion.button>

          <motion.p className="signup-link" variants={itemVariants}>
            Don't have an account? <a href="/register">Sign Up</a>
            <br />
          </motion.p>
        </form>
      </motion.div>
    </motion.div>
  );
}
